/**
 * leaseAidSlice — handles monthly rents (depots + office) and the bundle
 * of 3 yearly regional aids triggered at January 1st.
 *
 * State :
 *   activeAids: [{ id, activeUntilDay, grantedOnDay }]
 *     → aids currently in effect (their `activeUntilDay` is a totalDays cap)
 *   claimedOneShotAids: { [aidId]: boolean }
 *     → one-shot aids (cash subsidies) marked when claimed — never given twice
 *   yearStartSnapshot: { drivers, supports, capexLifetime, totalDays }
 *     → snapshot taken at year-start so we can compute year-over-year deltas
 *
 * Helpers exposed on the store :
 *   getActiveAids()                    → list of currently active aids
 *   isAidActive(aidId)                 → boolean
 *   computeMonthlyRent()               → { gross, byCategory, aidsApplied, net }
 *   evaluateYearStartAids({pushMessage}) → fires at Jan 1st (called by coreSlice)
 *
 * 28/05/2026 v2 — Phase A + B implementation.
 */
import { LEASE_RATES, REGIONAL_AIDS, AID_BY_ID } from '../../data/leaseAndAids';

const DAYS_IN_YEAR = 360;

function ownedDepotCounts(state) {
  // The HQ slot is always active. Built secondary depots and acquired
  // (M&A) depots come from `state.ownedDepots` (see depotEconomics).
  const ownedDepots = state.ownedDepots || [];
  // The first slot is the HQ (terrain de base + garage). Built depots
  // 2-3 are `kind === 'built'` (excluding HQ which has no entry). Acquired
  // depots 4-6 are `kind === 'acquired'`.
  let secondary = 0;
  let acquired = 0;
  for (const d of ownedDepots) {
    if (d.kind === 'acquired') acquired += 1;
    else if (d.kind === 'built' && !d.isHQ) secondary += 1;
  }
  return { hq: 1, secondary, acquired };
}

export const createLeaseAidSlice = (set, get) => ({
  activeAids: [],
  claimedOneShotAids: {},
  yearStartSnapshot: null,

  // ──────────────────────────────────────────────────────────────────
  // Lease : compute the monthly rent breakdown.
  // ──────────────────────────────────────────────────────────────────
  computeMonthlyRent: () => {
    const state = get();
    const counts = ownedDepotCounts(state);
    const officeUnlocked = !!state.officeUnlockedAt;
    const byCategory = {
      hq:        counts.hq        * LEASE_RATES.hq,
      secondary: counts.secondary * LEASE_RATES.secondary,
      acquired:  counts.acquired  * LEASE_RATES.acquired,
      office:    officeUnlocked   ? LEASE_RATES.office : 0,
    };
    const gross = byCategory.hq + byCategory.secondary + byCategory.acquired + byCategory.office;
    // Apply aids
    const activeAidIds = new Set((state.activeAids || []).filter(
      a => (state.totalDays || 0) <= a.activeUntilDay
    ).map(a => a.id));
    const aidsApplied = [];
    let net = gross;
    if (activeAidIds.has('aid-hq-rent')) {
      aidsApplied.push({ id: 'aid-hq-rent', saved: byCategory.hq });
      net -= byCategory.hq;
    }
    if (activeAidIds.has('aid-office-rent')) {
      aidsApplied.push({ id: 'aid-office-rent', saved: byCategory.office });
      net -= byCategory.office;
    }
    return {
      gross,
      byCategory,
      counts,
      officeUnlocked,
      aidsApplied,
      net: Math.max(0, Math.round(net)),
    };
  },

  // ──────────────────────────────────────────────────────────────────
  // Aids : selector helpers.
  // ──────────────────────────────────────────────────────────────────
  getActiveAids: () => {
    const state = get();
    const today = state.totalDays || 0;
    return (state.activeAids || []).filter(a => today <= a.activeUntilDay);
  },

  isAidActive: (aidId) => {
    const state = get();
    const today = state.totalDays || 0;
    return (state.activeAids || []).some(a => a.id === aidId && today <= a.activeUntilDay);
  },

  /**
   * Tax-side helper : CFE exemption reduces the effective IS / TVA tail
   * by 15% during the active window. Returns a multiplier in [0.85, 1].
   */
  getCfeMultiplier: () => {
    const state = get();
    const today = state.totalDays || 0;
    const cfeAid = (state.activeAids || []).find(
      a => a.id === 'aid-cfe-exoneration' && today <= a.activeUntilDay
    );
    if (!cfeAid) return 1;
    const def = AID_BY_ID['aid-cfe-exoneration'];
    return 1 - (def.benefit.percent / 100);
  },

  // ──────────────────────────────────────────────────────────────────
  // Year-start snapshot + aid evaluation. Called by coreSlice on Jan 1.
  // ──────────────────────────────────────────────────────────────────
  evaluateYearStartAids: () => {
    const state = get();
    const today = state.totalDays || 0;
    const previousSnap = state.yearStartSnapshot;
    const driversNow = (state.drivers || []).length;
    const supportsNow = Object.keys(state.hiredSupport || {}).length;
    const capexLifetimeNow = state.lifetimeCapexSpent || 0;

    // First year-end : initial snapshot, no aid evaluation yet.
    if (!previousSnap) {
      set({ yearStartSnapshot: { drivers: driversNow, supports: supportsNow, capexLifetime: capexLifetimeNow, totalDays: today } });
      return { triggered: [], messages: [] };
    }

    // Compute deltas vs previous Jan 1.
    const netHires = (driversNow + supportsNow) - (previousSnap.drivers + previousSnap.supports);
    const capexThisYear = capexLifetimeNow - previousSnap.capexLifetime;

    const triggered = [];
    const messages = [];
    const newActiveAids = [...(state.activeAids || [])];
    const claimed = { ...(state.claimedOneShotAids || {}) };
    let treasuryDelta = 0;

    for (const aid of REGIONAL_AIDS) {
      let conditionMet = false;
      let conditionLabel = '';
      switch (aid.condition.type) {
        case 'net_hires_min':
          conditionMet = netHires >= aid.condition.value;
          conditionLabel = `${netHires} emploi(s) CDI net(s) créé(s) sur l'année (seuil : ${aid.condition.value})`;
          break;
        case 'support_min':
          conditionMet = supportsNow >= aid.condition.value;
          conditionLabel = `${supportsNow} fonction(s) support recrutée(s) (seuil : ${aid.condition.value})`;
          break;
        case 'capex_year_min':
          conditionMet = capexThisYear >= aid.condition.value;
          conditionLabel = `${capexThisYear.toLocaleString('fr-FR')} € de CAPEX sur l'année (seuil : ${aid.condition.value.toLocaleString('fr-FR')} €)`;
          break;
        default:
          conditionMet = false;
      }

      if (!conditionMet) continue;

      // Aid granted — extend or start its validity window.
      const activeUntilDay = today + aid.durationMonths * 30;
      const existing = newActiveAids.findIndex(a => a.id === aid.id);
      if (existing >= 0) {
        newActiveAids[existing] = { ...newActiveAids[existing], activeUntilDay, grantedOnDay: today };
      } else {
        newActiveAids.push({ id: aid.id, activeUntilDay, grantedOnDay: today });
      }

      // One-shot cash benefit (FEDER) — pay only once per aid.
      let oneShotPaid = 0;
      if (aid.benefit.oneShot && !claimed[aid.id]) {
        oneShotPaid = aid.benefit.oneShot;
        treasuryDelta += oneShotPaid;
        claimed[aid.id] = true;
      }

      triggered.push({ id: aid.id, name: aid.name, oneShotPaid, condition: conditionLabel });

      // Build a Messagerie message.
      const oneShotLine = oneShotPaid > 0
        ? `\n\nVersement immédiat : **${oneShotPaid.toLocaleString('fr-FR')} €** crédité sur votre trésorerie.`
        : '';
      const body =
        `Madame, Monsieur,\n\n` +
        `Nous avons le plaisir de vous informer que votre entreprise est éligible à l'aide « **${aid.name}** ».\n\n` +
        `**Condition vérifiée :** ${conditionLabel}.\n\n` +
        `**Détail :** ${aid.description}` +
        oneShotLine +
        `\n\nCette aide est active jusqu'au jour ${activeUntilDay} (≈ ${aid.durationMonths} mois). Vous pouvez la retrouver dans **Finance → Loyers & Aides**.\n\n` +
        `Cordialement,\nServices Économiques — Conseil Régional`;

      messages.push({
        senderType: 'support',
        senderName: aid.sender,
        senderRole: aid.senderRole,
        tone: 'positive',
        kind: 'info',
        subject: `Aide régionale accordée — ${aid.name}`,
        body,
        ttlDays: 60,
      });
    }

    set({
      activeAids: newActiveAids,
      claimedOneShotAids: claimed,
      yearStartSnapshot: { drivers: driversNow, supports: supportsNow, capexLifetime: capexLifetimeNow, totalDays: today },
      treasury: (state.treasury || 0) + treasuryDelta,
    });

    return { triggered, messages, netHires, capexThisYear, supportsNow };
  },

  /**
   * Cheaper variant exposed for the UI : returns the same evaluation but
   * NEVER mutates state. Used by the « Aides régionales » preview panel
   * so the player sees in real-time which aids they'd unlock at next Jan 1.
   */
  previewYearStartAids: () => {
    const state = get();
    const snap = state.yearStartSnapshot;
    const driversNow = (state.drivers || []).length;
    const supportsNow = Object.keys(state.hiredSupport || {}).length;
    const capexLifetimeNow = state.lifetimeCapexSpent || 0;
    const netHires = snap ? (driversNow + supportsNow) - (snap.drivers + snap.supports) : null;
    const capexThisYear = snap ? capexLifetimeNow - snap.capexLifetime : null;
    return REGIONAL_AIDS.map(aid => {
      let actual, threshold = aid.condition.value, label = '';
      switch (aid.condition.type) {
        case 'net_hires_min':
          actual = netHires;
          label = 'Emplois nets créés depuis le 1er janvier';
          break;
        case 'support_min':
          actual = supportsNow;
          label = 'Fonctions support recrutées';
          break;
        case 'capex_year_min':
          actual = capexThisYear;
          label = 'CAPEX investi depuis le 1er janvier';
          break;
        default:
          actual = null;
      }
      return { aid, actual, threshold, label, eligible: actual != null && actual >= threshold };
    });
  },
});
